import { useNavigate } from "react-router-dom";
import {
  useGetPurchaseOrderQuery,
  useUpdatePurchaseOrderMutation,
  useDeletePurchaseOrderMutation,
  useCancelPurchaseOrderMutation,
  useLazyGetPurchaseOrderPdfQuery,
} from "@/redux/services/inventory/purchaseOrder.api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import DataTable, { Column } from "@/components/ui/DataTable";
import { cn, displayPrice, formatStock } from "@/lib/utils";
import { useRestaurantCurrency, useToggle } from "@/lib/hooks";
import { useAppSelector } from "@/redux/features/hooks";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Ban,
  Copy,
  Download,
  Pencil,
  Trash2,
  AlertTriangle,
  Wallet,
} from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { usePermissions } from "@/hooks/usePermissions";
import { canRecordPurchaseOrders } from "@/lib/permissions";
import toast from "react-hot-toast";
import PurchaseOrderStatusBadge from "./PurchaseOrderStatusBadge";
import ReceiveGoodsModal from "./ReceiveGoodsModal";
import EditPurchaseOrderModal from "./EditPurchaseOrderModal";
import PayPurchaseOrderModal from "./PayPurchaseOrderModal";
import OverduePill, { isPurchaseOrderOverdue } from "./OverduePill";
import { PurchaseOrderLine } from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

interface PurchaseOrderDetailProps {
  uuid: string;
}

const PurchaseOrderDetail = ({ uuid }: PurchaseOrderDetailProps) => {
  const navigate = useNavigate();
  const currency = useRestaurantCurrency();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const { data: po, isLoading } = useGetPurchaseOrderQuery({ uuid });
  const [updatePO, { isLoading: isUpdating }] =
    useUpdatePurchaseOrderMutation();
  const [deletePO] = useDeletePurchaseOrderMutation();
  const [cancelPO] = useCancelPurchaseOrderMutation();
  const [fetchPdf, { isFetching: isFetchingPdf }] =
    useLazyGetPurchaseOrderPdfQuery();
  const { isOpen: isReceiveOpen, open: openReceive, close: closeReceive } =
    useToggle();
  const { isOpen: isEditOpen, open: openEdit, close: closeEdit } = useToggle();
  const { isOpen: isPayOpen, open: openPay, close: closePay } = useToggle();
  const confirm = useConfirm();
  const { can, canManage } = usePermissions();
  // Purchase orders belong to the suppliers domain in the API
  // (PERMISSION_SUPPLIERS: suppliers.view to read, suppliers.manage to write).
  const canManagePurchaseOrders = canManage("suppliers");
  // Duplicating opens the purchase form (SFRESTO-165).
  const canDuplicate = canRecordPurchaseOrders(can);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="w-full h-24" />
        <Skeleton className="w-full h-40" />
      </div>
    );
  }

  if (!po) {
    return (
      <div className="dash__card text-center py-12">
        <p className="text-muted-foreground">Bon de commande introuvable</p>
      </div>
    );
  }

  const handleSend = async () => {
    const res = await updatePO({ uuid, data: { status: "sent" } });
    if ("data" in res) {
      toast.success("Bon de commande envoyé");
    } else {
      toastApiError(res.error, "Erreur lors de l'envoi");
    }
  };

  const handleCancel = async () => {
    // Received / partially_received POs already moved stock, so cancelling them
    // goes through the dedicated endpoint that reverses it. Draft / sent never
    // touched stock and just flip status via PATCH.
    const reversesStock =
      po.status === "received" || po.status === "partially_received";
    const ok = await confirm({
      title: "Annuler ce bon de commande ?",
      description: reversesStock
        ? "Cette action annule le bon et retire du stock les quantités déjà reçues."
        : "Cette action marque le bon comme annulé.",
      confirmText: "Annuler le bon",
      cancelText: "Retour",
      variant: "destructive",
      icon: <AlertTriangle className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = reversesStock
      ? await cancelPO({ uuid })
      : await updatePO({ uuid, data: { status: "cancelled" } });
    if ("data" in res) {
      toast.success("Bon de commande annulé");
    } else {
      toastApiError(res.error, "Erreur lors de l'annulation");
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Supprimer ${po.poNumber} ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await deletePO({ uuid });
    if ("error" in res) {
      toastApiError(res.error, "Erreur lors de la suppression");
      return;
    }
    toast.success("Bon de commande supprimé");
    navigate("/inventory/purchase-orders");
  };

  const canSend = po.status === "draft";
  // Drafts get the full edit; once stock has moved (partially_received or
  // received) only the purchase date and notes can change — supplier/lines lock.
  const canEdit =
    po.status === "draft" ||
    po.status === "received" ||
    po.status === "partially_received";
  const isLimitedEdit = po.status !== "draft";
  const canReceive =
    po.status === "sent" || po.status === "partially_received";
  const canCancel =
    po.status === "draft" ||
    po.status === "sent" ||
    po.status === "received" ||
    po.status === "partially_received";
  const canDelete = po.status === "draft";
  // Payment is allowed once the PO has been sent (not draft/cancelled) and only once.
  const canPay =
    !po.paidAt &&
    (po.status === "sent" ||
      po.status === "partially_received" ||
      po.status === "received");

  const showProgress =
    po.status === "sent" ||
    po.status === "partially_received" ||
    po.status === "received";

  const totalReceivedAmount = po.lines.reduce((acc, line) => {
    const received = parseFloat(line.quantityReceived) || 0;
    return acc + received * line.unitPrice;
  }, 0);
  const overallProgress =
    po.totalAmount > 0
      ? Math.min(100, (totalReceivedAmount / po.totalAmount) * 100)
      : 0;

  const handleDuplicate = () => {
    navigate(`/inventory/purchase-orders/new?from=${po.id}`);
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await fetchPdf({ uuid }).unwrap();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bon-de-commande-${po.poNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="dash__card space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-card-foreground">
                  {po.poNumber}
                </h2>
                <PurchaseOrderStatusBadge status={po.status} />
                {isPurchaseOrderOverdue(po) && <OverduePill />}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {po.supplier?.name}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canSend && canManagePurchaseOrders && (
                <Button
                  type="button"
                  size="sm"
                  isLoading={isUpdating}
                  onClick={handleSend}
                >
                  Valider
                </Button>
              )}
              {canReceive && canManagePurchaseOrders && (
                <Button type="button" size="sm" onClick={openReceive}>
                  Recevoir
                </Button>
              )}
              {canPay && canManagePurchaseOrders && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={openPay}
                >
                  <Wallet className="h-4 w-4" />
                  Marquer comme payé
                </Button>
              )}
              {po.paidAt && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">
                  <Wallet className="h-3.5 w-3.5" />
                  Payé
                </span>
              )}
              {canEdit && canManagePurchaseOrders && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={openEdit}
                >
                  <Pencil className="h-4 w-4" />
                  Modifier
                </Button>
              )}
              {canCancel && canManagePurchaseOrders && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                >
                  <Ban className="h-4 w-4" />
                  Annuler
                </Button>
              )}
              {canDuplicate && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleDuplicate}
                >
                  <Copy className="h-4 w-4" />
                  Dupliquer
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleDownloadPdf}
                isLoading={isFetchingPdf}
              >
                <Download className="h-4 w-4" />
                PDF
              </Button>
              {canDelete && canManagePurchaseOrders && (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  Supprimer
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Date de commande</p>
              <p className="text-sm font-medium">
                {format(parseISO(po.orderDate), "d MMMM yyyy", { locale: fr })}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Livraison prévue</p>
              <p className="text-sm font-medium">
                {po.expectedDeliveryDate
                  ? format(parseISO(po.expectedDeliveryDate), "d MMMM yyyy", {
                      locale: fr,
                    })
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Reçu le</p>
              <p className="text-sm font-medium">
                {po.receivedAt
                  ? format(parseISO(po.receivedAt), "d MMMM yyyy", {
                      locale: fr,
                    })
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-sm font-semibold">
                {displayPrice(po.totalAmount, currency)}
              </p>
            </div>
          </div>

          {showProgress && (
            <div className="pt-2 border-t border-border space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Réception</p>
                <p className="text-xs font-medium">
                  {displayPrice(totalReceivedAmount, currency)} /{" "}
                  {displayPrice(po.totalAmount, currency)} ·{" "}
                  {overallProgress.toFixed(0)}%
                </p>
              </div>
              <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full transition-all",
                    overallProgress >= 100 ? "bg-green-500" : "bg-blue-500"
                  )}
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}

          {po.notes && (
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{po.notes}</p>
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-card-foreground mb-2">
            Lignes ({po.lines.length})
          </h3>
          <DataTable
            data={po.lines}
            columns={
              [
                {
                  key: "ingredient",
                  label: "Ingrédient",
                  formatter: (_v, row: PurchaseOrderLine) => (
                    <span className="font-medium">
                      {row.ingredient.name}
                    </span>
                  ),
                },
                {
                  key: "quantityOrdered",
                  label: "Commandé",
                  formatter: (_v, row: PurchaseOrderLine) => {
                    if (
                      row.packaging &&
                      row.packageQuantityOrdered !== null
                    ) {
                      return (
                        <div>
                          <div>
                            {parseFloat(row.packageQuantityOrdered)}{" "}
                            {row.packaging.name}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            ={" "}
                            {formatStock(
                              row.quantityOrdered,
                              row.ingredient.baseUnit
                            )}
                          </div>
                        </div>
                      );
                    }
                    return formatStock(
                      row.quantityOrdered,
                      row.ingredient.baseUnit
                    );
                  },
                },
                {
                  key: "quantityReceived",
                  label: "Reçu",
                  formatter: (_v, row: PurchaseOrderLine) => {
                    const usePackaging =
                      !!row.packaging && row.packageQuantityOrdered !== null;
                    const orderedNum = usePackaging
                      ? parseFloat(row.packageQuantityOrdered ?? "0") || 0
                      : parseFloat(row.quantityOrdered) || 0;
                    const receivedNum = usePackaging
                      ? parseFloat(row.packageQuantityReceived ?? "0") || 0
                      : parseFloat(row.quantityReceived) || 0;
                    const pct =
                      orderedNum > 0
                        ? Math.min(100, (receivedNum / orderedNum) * 100)
                        : 0;
                    return (
                      <div className="min-w-[120px] space-y-1">
                        <div className="text-xs">
                          {usePackaging ? (
                            <>
                              {receivedNum} {row.packaging?.name}
                            </>
                          ) : (
                            formatStock(
                              row.quantityReceived,
                              row.ingredient.baseUnit
                            )
                          )}
                        </div>
                        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full transition-all",
                              pct >= 100 ? "bg-green-500" : "bg-blue-500"
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  },
                },
                {
                  key: "unitPrice",
                  label: "Prix unitaire",
                  formatter: (_v, row: PurchaseOrderLine) => {
                    if (row.packaging && row.packageUnitPrice !== null) {
                      return (
                        <div>
                          <div>
                            {displayPrice(row.packageUnitPrice, currency)} /{" "}
                            {row.packaging.name}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            ≈ {displayPrice(row.unitPrice, currency)} /{" "}
                            {row.ingredient.baseUnit}
                          </div>
                        </div>
                      );
                    }
                    return displayPrice(row.unitPrice, currency);
                  },
                },
                {
                  key: "lineTotal",
                  label: "Total ligne",
                  formatter: (_v, row: PurchaseOrderLine) => (
                    <span className="font-medium">
                      {displayPrice(row.lineTotal, currency)}
                    </span>
                  ),
                },
              ] as Column[]
            }
            totalCount={po.lines.length}
            searchable={false}
            paginated={false}
          />
        </div>
      </div>

      {isReceiveOpen && (
        <ReceiveGoodsModal
          isOpen={isReceiveOpen}
          onClose={closeReceive}
          purchaseOrder={po}
        />
      )}

      {isEditOpen && restaurant && (
        <EditPurchaseOrderModal
          isOpen={isEditOpen}
          onClose={closeEdit}
          purchaseOrder={po}
          restaurantId={restaurant.id}
          limited={isLimitedEdit}
        />
      )}

      {isPayOpen && (
        <PayPurchaseOrderModal
          isOpen={isPayOpen}
          onClose={closePay}
          purchaseOrder={po}
        />
      )}
    </>
  );
};

export default PurchaseOrderDetail;
